/**
 * Portfolio-monitoring template API (DOP-447..453).
 *
 * The only place that talks to `/api/monitoring`. Every response is parsed
 * at the boundary with the schemas in `types/monitoring.ts`; a malformed
 * payload throws `MonitoringMalformedResponseError` so a page shows an error
 * state instead of rendering an unknown shape. A 409 (locked run, open gaps,
 * a job already running) throws `MonitoringConflictError` carrying the
 * server's detail, which the page shows verbatim. Every other non-2xx throws
 * `MonitoringRequestError`.
 */

import {
	type MonitoringBoard,
	type MonitoringBorrower,
	type MonitoringBorrowerCandidateGroup,
	type MonitoringCompare,
	type MonitoringCreateBorrowerPayload,
	type MonitoringCreatePeriodPayload,
	type MonitoringCreateTemplatePayload,
	type MonitoringLockPayload,
	type MonitoringPeriod,
	type MonitoringRun,
	type MonitoringRunPeriodPayload,
	type MonitoringRunRequestResult,
	type MonitoringRunVersion,
	type MonitoringSetFigurePayload,
	type MonitoringTemplate,
	type MonitoringUpdateTemplatePayload,
	parseMonitoringBoard,
	parseMonitoringBorrower,
	parseMonitoringBorrowerCandidateGroupList,
	parseMonitoringBorrowerList,
	parseMonitoringCompare,
	parseMonitoringPeriod,
	parseMonitoringPeriodList,
	parseMonitoringRun,
	parseMonitoringRunRequestResult,
	parseMonitoringRunVersionList,
	parseMonitoringTemplate,
	parseMonitoringTemplateList,
} from "../types/monitoring";
import { apiFetch } from "../utils/apiClient";

const BASE = "/api/monitoring";
const HTTP_CONFLICT = 409;

export class MonitoringConflictError extends Error {
	constructor(detail: string) {
		super(detail);
		this.name = "MonitoringConflictError";
	}
}

export class MonitoringRequestError extends Error {
	readonly status: number;
	constructor(status: number, detail: string) {
		super(detail);
		this.name = "MonitoringRequestError";
		this.status = status;
	}
}

async function detailOf(response: Response): Promise<string> {
	try {
		const body = await response.json();
		if (body && typeof body.detail === "string") return body.detail;
		if (body && body.detail) return JSON.stringify(body.detail);
	} catch {
		// no JSON body
	}
	return response.statusText || `HTTP ${response.status}`;
}

async function request(
	endpoint: string,
	options: RequestInit = {},
): Promise<Response> {
	const response = await apiFetch(endpoint, options);
	if (response.ok) return response;
	const detail = await detailOf(response);
	if (response.status === HTTP_CONFLICT) {
		throw new MonitoringConflictError(detail);
	}
	throw new MonitoringRequestError(response.status, detail);
}

async function requestJson(
	endpoint: string,
	options: RequestInit = {},
): Promise<unknown> {
	const response = await request(endpoint, options);
	return response.json();
}

function jsonBody(payload: unknown): RequestInit {
	return {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	};
}

// ----------------------------------------------------------------- templates

export async function listTemplates(): Promise<MonitoringTemplate[]> {
	return parseMonitoringTemplateList(await requestJson(`${BASE}/templates`));
}

export async function createTemplate(
	payload: MonitoringCreateTemplatePayload,
): Promise<MonitoringTemplate> {
	return parseMonitoringTemplate(
		await requestJson(`${BASE}/templates`, jsonBody(payload)),
	);
}

export async function getTemplate(
	templateId: string,
): Promise<MonitoringTemplate> {
	return parseMonitoringTemplate(
		await requestJson(`${BASE}/templates/${encodeURIComponent(templateId)}`),
	);
}

/**
 * Edit the recipe (DOP-456). PATCH, so a caller changing one setting cannot
 * blank the rest; the response is the whole template, which the caller stores
 * in place of its copy rather than trusting its own optimistic edit.
 */
export async function updateTemplate(
	templateId: string,
	patch: MonitoringUpdateTemplatePayload,
): Promise<MonitoringTemplate> {
	return parseMonitoringTemplate(
		await requestJson(`${BASE}/templates/${encodeURIComponent(templateId)}`, {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(patch),
		}),
	);
}

/**
 * Delete the workflow: the template, its borrowers, periods, runs and ledgers.
 *
 * Throws :class:`MonitoringConflictError` on 409, which the backend returns
 * only while a run is still generating — the one state where deleting would
 * strand a worker. Every other refusal is an ordinary request error.
 */
export async function deleteTemplate(templateId: string): Promise<void> {
	await request(`${BASE}/templates/${encodeURIComponent(templateId)}`, {
		method: "DELETE",
	});
}

// ----------------------------------------------------------------- borrowers

export async function listBorrowers(
	templateId: string,
): Promise<MonitoringBorrower[]> {
	return parseMonitoringBorrowerList(
		await requestJson(
			`${BASE}/templates/${encodeURIComponent(templateId)}/borrowers`,
		),
	);
}

/**
 * The borrower names the caller's own documents already carry, by folder.
 *
 * What the picker offers instead of a free-text name (DOP-454): the package
 * resolver matches on the filename prefix, so a name that is not one of these
 * is a borrower whose package never arrives.
 */
export async function listBorrowerCandidates(
	templateId: string,
): Promise<MonitoringBorrowerCandidateGroup[]> {
	return parseMonitoringBorrowerCandidateGroupList(
		await requestJson(
			`${BASE}/templates/${encodeURIComponent(templateId)}/borrower-candidates`,
		),
	);
}

export async function createBorrower(
	templateId: string,
	payload: MonitoringCreateBorrowerPayload,
): Promise<MonitoringBorrower> {
	return parseMonitoringBorrower(
		await requestJson(
			`${BASE}/templates/${encodeURIComponent(templateId)}/borrowers`,
			jsonBody(payload),
		),
	);
}

// ----------------------------------------------------------------- periods

export async function listPeriods(
	templateId: string,
): Promise<MonitoringPeriod[]> {
	return parseMonitoringPeriodList(
		await requestJson(
			`${BASE}/templates/${encodeURIComponent(templateId)}/periods`,
		),
	);
}

export async function createPeriod(
	templateId: string,
	payload: MonitoringCreatePeriodPayload,
): Promise<MonitoringPeriod> {
	return parseMonitoringPeriod(
		await requestJson(
			`${BASE}/templates/${encodeURIComponent(templateId)}/periods`,
			jsonBody(payload),
		),
	);
}

export async function getBoard(periodId: string): Promise<MonitoringBoard> {
	return parseMonitoringBoard(
		await requestJson(`${BASE}/periods/${encodeURIComponent(periodId)}/board`),
	);
}

/** Queues generation for the period's borrowers; locked runs are skipped. */
export async function runPeriod(
	periodId: string,
	payload: MonitoringRunPeriodPayload = {},
): Promise<MonitoringRunRequestResult> {
	return parseMonitoringRunRequestResult(
		await requestJson(
			`${BASE}/periods/${encodeURIComponent(periodId)}/runs`,
			jsonBody(payload),
		),
	);
}

// ----------------------------------------------------------------- runs

export async function getRun(runId: string): Promise<MonitoringRun> {
	return parseMonitoringRun(
		await requestJson(`${BASE}/runs/${encodeURIComponent(runId)}`),
	);
}

/** Manual entry for one figure (or accept it as not available). */
export async function setFigure(
	runId: string,
	figureKey: string,
	payload: MonitoringSetFigurePayload,
): Promise<MonitoringRun> {
	return parseMonitoringRun(
		await requestJson(
			`${BASE}/runs/${encodeURIComponent(runId)}/figures/${encodeURIComponent(figureKey)}`,
			{ ...jsonBody(payload), method: "PATCH" },
		),
	);
}

export async function reviewRun(runId: string): Promise<MonitoringRun> {
	return parseMonitoringRun(
		await requestJson(`${BASE}/runs/${encodeURIComponent(runId)}/review`, {
			method: "POST",
		}),
	);
}

export async function lockRun(
	runId: string,
	payload: MonitoringLockPayload = {},
): Promise<MonitoringRun> {
	return parseMonitoringRun(
		await requestJson(
			`${BASE}/runs/${encodeURIComponent(runId)}/lock`,
			jsonBody(payload),
		),
	);
}

/** Prior-vs-current; 404 (no locked prior) surfaces as MonitoringRequestError. */
export async function getCompare(runId: string): Promise<MonitoringCompare> {
	return parseMonitoringCompare(
		await requestJson(`${BASE}/runs/${encodeURIComponent(runId)}/compare`),
	);
}

export async function listRunVersions(
	runId: string,
): Promise<MonitoringRunVersion[]> {
	return parseMonitoringRunVersionList(
		await requestJson(`${BASE}/runs/${encodeURIComponent(runId)}/versions`),
	);
}

// ----------------------------------------------------------------- export

export interface MonitoringExportFile {
	blob: Blob;
	filename: string;
}

function filenameFrom(response: Response, fallback: string): string {
	const header = response.headers.get("Content-Disposition") ?? "";
	const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
	return match ? decodeURIComponent(match[1]) : fallback;
}

/**
 * The workbook comes back through the API proxy as a blob so the browser
 * saves it under the server's filename (a cross-origin anchor ignores the
 * download attribute — DOP-425).
 */
export async function exportPeriod(
	periodId: string,
): Promise<MonitoringExportFile> {
	const response = await request(
		`${BASE}/periods/${encodeURIComponent(periodId)}/export.xlsx`,
	);
	return {
		blob: await response.blob(),
		filename: filenameFrom(response, "covenant_monitoring.xlsx"),
	};
}

export async function exportRun(runId: string): Promise<MonitoringExportFile> {
	const response = await request(
		`${BASE}/runs/${encodeURIComponent(runId)}/export.xlsx`,
	);
	return {
		blob: await response.blob(),
		filename: filenameFrom(response, "covenant_monitoring.xlsx"),
	};
}

/** Saves a blob under a filename without a cross-origin anchor. */
export function saveExportFile(file: MonitoringExportFile): void {
	const url = URL.createObjectURL(file.blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = file.filename;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
}

export const monitoringService = {
	listTemplates,
	createTemplate,
	getTemplate,
	updateTemplate,
	deleteTemplate,
	listBorrowers,
	listBorrowerCandidates,
	createBorrower,
	listPeriods,
	createPeriod,
	getBoard,
	runPeriod,
	getRun,
	setFigure,
	reviewRun,
	lockRun,
	getCompare,
	listRunVersions,
	exportPeriod,
	exportRun,
	saveExportFile,
};
